/*
 * API Endpoint Constants
 *
 * Features:
 * - Central route keys consumed by the frontend dataService
 * - Keeps UI code aligned with API data-route registration
 *
 * Changes:
 * - 2026-09-09: Added user access-level updates and server-resolved note visibility policy
 * - 2026-05-20: Added current-user PAT status and clear endpoints
 * - 2026-06-21: Added AI Inbox endpoints for the operational queue page
 * - 2026-09-08: Added bounded Notes page, count, calendar, and detail endpoints
 */
export const API_ENDPOINTS = {
  // Auth routes
  GET_AUTH_WHO: 'get /api/data/who',

  // User routes
  GET_USERS: 'get /api/data/users',
  PUT_USER_ACCESS_LEVEL: 'put /api/data/users/:userId/access-level',
  GET_USER_PAT: 'get /api/data/users/me/pat',
  DELETE_USER_PAT: 'delete /api/data/users/me/pat',
  POST_USER_ACCOUNT: 'post /api/data/users/:userId/accounts',
  DELETE_USER_ACCOUNT: 'delete /api/data/users/:userId/accounts/:id',
  GET_USER_CONTACTS: 'get /api/data/users/:userId/contacts',
  POST_USER_CONTACT: 'post /api/data/users/:userId/contacts',
  DELETE_USER_CONTACT: 'delete /api/data/users/:userId/contacts/:id',
  GET_USER_NOTES: 'get /api/data/users/me/notes', // New endpoint for user notes
  GET_USER_NOTES_PAGE: 'get /api/data/users/me/notes/page',
  GET_USER_NOTES_COUNT: 'get /api/data/users/me/notes/count',
  GET_USER_NOTES_CALENDAR: 'get /api/data/users/me/notes/calendar',

  // Account routes
  GET_ACCOUNTS_SEARCH: 'get /api/data/accounts/search',
  GET_ACCOUNTS_COUNT: 'get /api/data/accounts/count',
  GET_ACCOUNTS: 'get /api/data/accounts',
  GET_ACCOUNT: 'get /api/data/accounts/:id',
  POST_ACCOUNT: 'post /api/data/accounts',
  PATCH_ACCOUNT: 'patch /api/data/accounts/:id',
  DELETE_ACCOUNT: 'delete /api/data/accounts/:id',
  GET_ACCOUNT_NOTES: 'get /api/data/accounts/:id/notes',
  GET_ACCOUNT_CONTACTS: 'get /api/data/accounts/:id/contacts',
  GET_USER_ACCOUNTS: 'get /api/data/users/:userId/accounts',
  GET_TEAM_ACCOUNTS: 'get /api/data/team/:teamId/accounts',

  // Contact routes
  GET_CONTACTS: 'get /api/data/contacts', // Get list of contacts with sorting options
  GET_CONTACT: 'get /api/data/contacts/:id', // Get details of a specific contact
  POST_CONTACT: 'post /api/data/contacts', // Create a new contact with provided details
  PUT_CONTACT: 'put /api/data/contacts', // Link existing RLP contact
  GET_CONTACTS_SEARCH_ALL: 'get /api/data/contacts/searchAll', // Search accounts and contacts for autocomplete
  GET_CONTACT_SEARCH: 'get /api/data/contacts/search', // Search contacts for autocomplete
  GET_CONTACT_COUNT: 'get /api/data/contacts/count', // Get total count of contacts in system
  GET_CONTACT_NOTES: 'get /api/data/contacts/:id/notes',
  PATCH_CONTACT: 'patch /api/data/contacts/:id', // Update an existing contact
  DELETE_CONTACT: 'delete /api/data/contacts/:id', // Delete a contact
  GET_TEAM_CONTACTS: 'get /api/data/team/:teamid/contacts', // Get list of contacts associated with a team

  // News routes
  GET_NEWS: 'get /api/data/news',
  POST_NEWS: 'post /api/data/news',

  // Note routes
  GET_NOTES: 'get /api/data/notes', // Generic notes endpoint for backwards compatibility
  GET_NOTES_COUNT: 'get /api/data/notes/count',
  GET_NOTE: 'get /api/data/notes/:id',
  GET_NOTE_VISIBILITY_POLICY: 'get /api/data/notes/visibility-policy',
  POST_NOTE: 'post /api/data/notes',
  PUT_NOTE: 'put /api/data/notes/:id',
  PATCH_NOTE: 'patch /api/data/notes/:id',
  PATCH_NOTE_STATUS: 'patch /api/data/notes/:id/status',
  DELETE_NOTE: 'delete /api/data/notes/:id',

  // Task routes
  GET_TASKS: 'get /api/data/tasks',
  GET_TASK: 'get /api/data/tasks/:id',
  POST_TASK: 'post /api/data/tasks',
  PATCH_TASK: 'patch /api/data/tasks/:id',
  PATCH_TASK_STATUS: 'patch /api/data/tasks/:id/status',
  DELETE_TASK: 'delete /api/data/tasks/:id',
  GET_ACCOUNT_TASKS: 'get /api/data/accounts/:id/tasks',
  GET_CONTACT_TASKS: 'get /api/data/contacts/:id/tasks',
  GET_NOTE_TASKS: 'get /api/data/notes/:noteId/tasks',

  // Inbox routes
  GET_INBOX: 'get /api/data/inbox',
  PATCH_INBOX: 'patch /api/data/inbox/:inboxId',
  DELETE_INBOX: 'delete /api/data/inbox/:inboxId',

  // Team routes
  GET_TEAMS: 'get /api/data/teams',
  GET_TEAM: 'get /api/data/teams/:id',
  DELETE_TEAM: 'delete /api/data/teams/:id',
  GET_TEAMS_USERS: 'get /api/data/teams/users',
  GET_TEAM_USERS: 'get /api/data/teams/:id/users',
  POST_TEAM_USER: 'post /api/data/teams/:id/users',
  DELETE_TEAM_USER: 'delete /api/data/teams/:id/users/:userId',

  // Schema routes
  GET_SCHEMA: 'get /api/data/schema',
  PATCH_SCHEMA: 'patch /api/data/schema',

  // Kanban routes
  GET_KANBAN_BOARDS: 'get /api/data/kanban',
  GET_KANBAN_BOARD: 'get /api/data/kanban/:id',
  GET_KANBAN_BOARDS_BY_SCHEMA: 'get /api/data/kanban/schema/:schemaId',
  POST_KANBAN_BOARD: 'post /api/data/kanban',
  PATCH_KANBAN_BOARD: 'patch /api/data/kanban/:id',
  DELETE_KANBAN_BOARD: 'delete /api/data/kanban/:id'
} as const;
